import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { CheckCircle, Users, Activity, Heart, ArrowRight, Shield, AlertCircle, TrendingUp, Stethoscope, ClipboardCheck, Calendar, BarChart3 } from 'lucide-react'
import authService from '@/lib/auth'
import patientAuthService from '@/lib/patient-auth'
import { PasscodeDialog } from '@/components/passcode-dialog'
import logoImage from '@/assets/breakthrough-health-logo.jpeg'

const staffLoginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

const patientLoginSchema = z.object({
  email: z.string().min(1, 'Email or phone number is required'),
  password: z.string().min(1, 'Password is required'),
})

type StaffLoginFormData = z.infer<typeof staffLoginSchema>
type PatientLoginFormData = z.infer<typeof patientLoginSchema>

const NAV_LINKS = [
  { label: 'What is Hep C', id: 'what-is-hepc' },
  { label: 'Global Impact', id: 'global-impact' },
  { label: 'About Us', id: 'about-us' },
  { label: 'Treatment Journey', id: 'treatment-journey' },
  { label: 'Our Platform', id: 'our-platform' },
  { label: 'Access Platform', id: 'access-platform' },
]

export default function Landing() {
  const [showPasscodeDialog, setShowPasscodeDialog] = useState(false)
  const [showLoginDialog, setShowLoginDialog] = useState(false)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState<'staff' | 'patient'>('staff')
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      const offset = 72
      const top = element.getBoundingClientRect().top + window.scrollY - offset
      window.scrollTo({ top, behavior: 'smooth' })
    }
    setMobileMenuOpen(false)
  }

  const staffForm = useForm<StaffLoginFormData>({
    resolver: zodResolver(staffLoginSchema),
  })

  const patientForm = useForm<PatientLoginFormData>({
    resolver: zodResolver(patientLoginSchema),
  })

  // Check if user is already authenticated
  useEffect(() => {
    const checkExistingAuth = () => {
      // Check staff auth
      const staffToken = authService.getToken()
      if (staffToken && !authService.isTokenExpired()) {
        router.visit('/clinician/dashboard', {
          replace: true,
          onError: () => {
            authService.clearTokens()
            checkPatientAuth()
          },
        })
        return
      }

      checkPatientAuth()
    }

    const checkPatientAuth = () => {
      // Check patient auth
      const patientToken = patientAuthService.getToken()
      if (patientToken) {
        router.visit('/patients/progress', {
          replace: true,
          onError: () => {
            patientAuthService.clearTokens()
            setIsCheckingAuth(false)
          },
        })
      } else {
        setIsCheckingAuth(false)
      }
    }

    checkExistingAuth()
  }, [])

  const onStaffSubmit = async (formData: StaffLoginFormData) => {
    setError('')

    try {
      const response = await fetch('/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (response.ok) {
        authService.handleLoginResponse(data)
        // Redirect based on user role
        const isAdmin = data.user?.admin || false
        const redirectUrl = isAdmin ? '/dashboard' : '/clinician/dashboard'
        router.visit(redirectUrl, { replace: true })
      } else {
        setError(data.error || 'Login failed')
      }
    } catch (err) {
      setError('An error occurred during login')
    }
  }

  const onPatientSubmit = async (formData: PatientLoginFormData) => {
    setError('')

    try {
      const response = await fetch('/patients/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (response.ok) {
        patientAuthService.handleLoginResponse(data)
        router.visit('/patients/progress', { replace: true })
      } else {
        setError(data.error || 'Login failed')
      }
    } catch (err) {
      setError('An error occurred during login')
    }
  }

  const handlePasscodeSuccess = () => {
    setShowPasscodeDialog(false)
    setShowLoginDialog(true)
  }

  if (isCheckingAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      {/* Navigation Bar */}
      <nav className="sticky top-0 z-50 border-b bg-white/80 backdrop-blur-sm dark:bg-gray-950/80 dark:border-gray-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src={logoImage}
                alt="Breakthrough Health"
                className="h-12 w-auto"
              />
            </div>

            {/* Desktop nav links */}
            <div className="hidden lg:flex items-center gap-1">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded-lg transition-all"
                >
                  {link.label}
                </button>
              ))}
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              <svg className="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                }
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t dark:border-gray-800 bg-white/95 dark:bg-gray-950/95 px-4 py-3 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="text-left px-3 py-2 text-sm font-medium text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded-lg transition-all"
              >
                {link.label}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-32">
        <div className="text-center">
          {/* Large Logo */}
          <div className="mb-8 flex justify-center animate-fade-in">
            <img
              src={logoImage}
              alt="Breakthrough Health Logo"
              className="h-40 w-auto md:h-52 lg:h-64 rounded-2xl shadow-2xl ring-4 ring-blue-100 dark:ring-blue-900/50"
            />
          </div>

          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/50 px-5 py-2 text-sm font-medium">
            <Heart className="size-4 text-blue-600 dark:text-blue-400" />
            <span className="text-blue-700 dark:text-blue-300">Together, we can end Hepatitis C</span>
          </div>
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
            Fixing the gap between diagnosis and cure in{' '}
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Hepatitis C care
            </span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
            A cure exists. The system is failing. Breakthrough Health ensures patients don't fall through the cracks.
          </p>
        </div>
      </section>

      {/* What is Hepatitis C */}
      <section id="what-is-hepc" className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <Card className="border-2 animate-scale-in">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="flex size-12 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <AlertCircle className="size-6 text-blue-600 dark:text-blue-400" />
              </div>
              <CardTitle className="text-3xl">What is Hepatitis C?</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-base text-muted-foreground">
            <p>
              Hepatitis C is a viral infection that causes liver inflammation. The virus is spread through
              contact with contaminated blood. Many people with Hepatitis C don't know they have it because
              they may not have symptoms for years, even decades.
            </p>
            <p>
              If left untreated, chronic Hepatitis C can lead to serious liver problems including cirrhosis
              (scarring of the liver), liver failure, and liver cancer. However, with today's treatments,
              most people can be cured.
            </p>
          </CardContent>
        </Card>
      </section>

      {/* Impact Statistics */}
      <section id="global-impact" className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <h2 className="mb-8 text-3xl font-bold text-center animate-fade-in">The Global Impact</h2>
        <div className="grid gap-6 md:grid-cols-3 stagger-animation">
          <Card className="transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
            <CardHeader>
              <div className="mb-4 flex size-12 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-600">
                <Users className="size-6 text-white" />
              </div>
              <CardTitle className="text-3xl">58 Million</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base">
                People living with chronic Hepatitis C worldwide
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
            <CardHeader>
              <div className="mb-4 flex size-12 items-center justify-center rounded-lg bg-gradient-to-br from-teal-500 to-teal-600">
                <TrendingUp className="size-6 text-white" />
              </div>
              <CardTitle className="text-3xl">95%+</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base">
                Cure rate with modern direct-acting antiviral treatments
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
            <CardHeader>
              <div className="mb-4 flex size-12 items-center justify-center rounded-lg bg-gradient-to-br from-red-500 to-red-600">
                <Activity className="size-6 text-white" />
              </div>
              <CardTitle className="text-3xl">290,000</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base">
                Annual deaths from Hepatitis C complications globally
              </CardDescription>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about-us" className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <Card className="border-2 bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-slate-900 dark:via-slate-800 dark:to-blue-950/30 shadow-xl animate-slide-in-up">
          <CardHeader>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex size-12 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-teal-600">
                <Heart className="size-6 text-white" />
              </div>
              <CardTitle className="text-3xl">About Us</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="prose prose-lg prose-slate dark:prose-invert max-w-none">
            <p className="text-base text-muted-foreground leading-relaxed mb-4">
              It's a strange irony of modern medicine: we possess the power to cure, but not the systems to deliver it. In an age where artificial intelligence can compose symphonies and forecast weather with staggering precision, we still let hundreds of thousands die from a disease we know how to cure. Why though? Hepatitis C is not an unsolvable riddle. It's not a medical mystery. It is a curable disease, trapped inside an incurable system failure.
            </p>
            <p className="text-base text-muted-foreground leading-relaxed mb-4 font-semibold">
              But that failure is not inevitable.
            </p>
            <p className="text-base text-muted-foreground leading-relaxed mb-4">
              We believe the true test of innovation isn't whether we can invent new miracles, it's whether we can bring known ones to those who need them most. Curing Hepatitis C does not require new science. It requires new infrastructure. It requires the courage to reach into the broken seams of our public health system and stitch together a path from diagnosis to cure, especially for those who have been ignored, overlooked, or forgotten.
            </p>
            <p className="text-base text-muted-foreground leading-relaxed mb-4">
              We know who they are: the man recently released from incarceration with no primary care. The woman struggling with substance use, cycling in and out of shelters. The child of immigrants who never gets tested because no one ever asks. These are not edge cases. They are the very center of the crisis. And they are the reason we are building Breakthrough Health. <span className="font-semibold italic">If not us, then who?</span>
            </p>
            <p className="text-base text-muted-foreground leading-relaxed mb-4">
              For many of those we serve, life rarely offers a win. A 12-week cure is more than a medical milestone; it is proof that progress is possible and that something finally went right. That single success can spark the confidence to take the next step toward recovery, housing, or employment, creating momentum that turns one victory into many. This is not just a platform. It is a call to redesign care, not around institutions, but around people.
            </p>
            <p className="text-base text-muted-foreground leading-relaxed mb-4">
              We are clinicians who have seen the gap up close. We are operators who know how to scale solutions. And we are partners looking to rebuild the broken bridge between medical possibility and public health reality.
            </p>
            <p className="text-base text-muted-foreground leading-relaxed mb-4">
              We believe technology, when grounded in compassion and community, can do more than automate tasks, it can extend reach. It can find those slipping through the cracks. It can transform detection into cure. But only if we build it with the right values, for the right reasons, and in the right places.
            </p>
            <p className="text-base text-muted-foreground leading-relaxed mb-4">
              This platform is just a start. A plan to turn clinical truth into public health transformation. A model not of what might be, but of what must be.
            </p>
            <p className="text-base text-muted-foreground leading-relaxed mb-6 font-semibold">
              Because when the cure already exists, failure is no longer a mystery. It's a choice.
            </p>
            <p className="text-base text-muted-foreground leading-relaxed font-semibold">
              And we choose to fix it.
            </p>
            <div className="mt-8 pt-6 border-t">
              <p className="text-base text-muted-foreground font-medium">
                Saumil J Patel, PharmD<br />
                Pooja Amin, PharmD
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Treatment Journey */}
      <section id="treatment-journey" className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <h2 className="mb-8 text-3xl font-bold text-center animate-fade-in">The Treatment Journey</h2>
        <div className="grid gap-6 md:grid-cols-4 stagger-animation">
          {[
            {
              icon: Stethoscope,
              title: 'Diagnosis',
              description: 'Blood tests to detect Hepatitis C antibodies and viral load',
            },
            {
              icon: ClipboardCheck,
              title: 'Assessment',
              description: 'Evaluation of liver damage and treatment planning',
            },
            {
              icon: Calendar,
              title: 'Treatment',
              description: '8-12 weeks of direct-acting antiviral medications',
            },
            {
              icon: CheckCircle,
              title: 'Cure',
              description: 'Over 95% cure rate with modern treatments',
            },
          ].map((step, idx) => (
            <Card key={step.title} className="relative transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:border-blue-300 dark:hover:border-blue-700">
              <div className="absolute -top-3 -left-3 flex size-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white font-bold text-sm">
                {idx + 1}
              </div>
              <CardHeader>
                <div className="mb-4 flex size-12 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <step.icon className="size-6 text-blue-600 dark:text-blue-400" />
                </div>
                <CardTitle className="text-lg">{step.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-sm">{step.description}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* About Platform */}
      <section id="our-platform" className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <Card className="border-2 bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-blue-950/30 dark:via-gray-900 dark:to-indigo-950/30">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="flex size-12 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600">
                <BarChart3 className="size-6 text-white" />
              </div>
              <CardTitle className="text-3xl">Our Platform</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-base text-muted-foreground">
              Breakthrough Health is a comprehensive treatment management platform designed for healthcare
              providers treating Hepatitis C patients. Our system helps clinicians track patient progress,
              manage treatment protocols, and monitor outcomes throughout the entire care journey.
            </p>
            <div className="grid gap-4 md:grid-cols-3">
              {[
                {
                  icon: ClipboardCheck,
                  title: 'Patient Management',
                  description: 'Complete patient records and treatment tracking',
                },
                {
                  icon: Calendar,
                  title: 'Treatment Plans',
                  description: 'Customizable protocols and appointment scheduling',
                },
                {
                  icon: BarChart3,
                  title: 'Outcome Monitoring',
                  description: 'Real-time tracking of viral loads and treatment success',
                },
              ].map((feature) => (
                <div
                  key={feature.title}
                  className="rounded-lg border bg-white/60 dark:bg-gray-900/60 p-4 transition-all duration-300 hover:bg-white dark:hover:bg-gray-900 hover:shadow-md hover:-translate-y-1"
                >
                  <feature.icon className="size-8 text-blue-600 dark:text-blue-400 mb-3" />
                  <h3 className="text-lg font-semibold mb-1">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Access Platform Section */}
      <section id="access-platform" className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <Card className="border-2 text-center">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <div className="flex size-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-indigo-600">
                <Shield className="size-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-3xl mb-2">Healthcare Provider Access</CardTitle>
            <CardDescription className="text-base max-w-2xl mx-auto">
              This platform is restricted to authorized healthcare providers. If you are a clinician
              treating Hepatitis C patients, please enter your access passcode to continue.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white animate-pulse-glow"
              onClick={() => setShowPasscodeDialog(true)}
            >
              Access Platform
            </Button>
            <p className="mt-4 text-sm text-muted-foreground">
              Protected access ensures patient data privacy and HIPAA compliance
            </p>
          </CardContent>
        </Card>
      </section>


      {/* Footer */}
      <footer className="border-t bg-white/50 backdrop-blur-sm dark:bg-gray-950/50 dark:border-gray-800">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-4 text-center text-sm text-muted-foreground md:flex-row md:justify-between">
            <p>&copy; 2026 Breakthrough Health. All rights reserved.</p>
            <div className="flex gap-6">
              <a
                href="mailto:info@breakthroughhealth.app"
                className="hover:text-foreground transition-colors"
              >
                Contact
              </a>
              <button
                onClick={() => router.visit('/privacy')}
                className="hover:text-foreground transition-colors"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => router.visit('/disclaimer')}
                className="hover:text-foreground transition-colors"
              >
                Terms of Service
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Passcode Dialog */}
      <PasscodeDialog
        open={showPasscodeDialog}
        onOpenChange={setShowPasscodeDialog}
        onSuccess={handlePasscodeSuccess}
      />

      {/* Login Dialog */}
      <Dialog open={showLoginDialog} onOpenChange={setShowLoginDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <img
                src={logoImage}
                alt="Breakthrough Health"
                className="h-20 w-auto"
              />
            </div>
            <DialogTitle>Welcome to Breakthrough Health</DialogTitle>
            <DialogDescription>Sign in to access your account</DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'staff' | 'patient')} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="staff">I'm a Clinician</TabsTrigger>
              <TabsTrigger value="patient">I'm a Patient</TabsTrigger>
            </TabsList>

            <TabsContent value="staff" className="mt-6">
              <form onSubmit={staffForm.handleSubmit(onStaffSubmit)} className="space-y-4">
                {error && activeTab === 'staff' && (
                  <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="staff-email">Email</Label>
                  <Input
                    id="staff-email"
                    type="email"
                    placeholder="clinician@example.com"
                    {...staffForm.register('email')}
                  />
                  {staffForm.formState.errors.email && (
                    <p className="text-sm text-destructive">{staffForm.formState.errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="staff-password">Password</Label>
                  <Input
                    id="staff-password"
                    type="password"
                    placeholder="••••••••"
                    {...staffForm.register('password')}
                  />
                  {staffForm.formState.errors.password && (
                    <p className="text-sm text-destructive">{staffForm.formState.errors.password.message}</p>
                  )}
                </div>

                <Button type="submit" className="w-full" disabled={staffForm.formState.isSubmitting}>
                  {staffForm.formState.isSubmitting ? 'Signing in...' : 'Sign In as Clinician'}
                </Button>

                <div className="text-center text-sm">
                  <a href="/password/forgot" className="text-muted-foreground hover:text-primary">
                    Forgot your password?
                  </a>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="patient" className="mt-6">
              <form onSubmit={patientForm.handleSubmit(onPatientSubmit)} className="space-y-4">
                {error && activeTab === 'patient' && (
                  <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="patient-email">Email or Phone Number</Label>
                  <Input
                    id="patient-email"
                    type="text"
                    placeholder="patient@example.com or +1234567890"
                    {...patientForm.register('email')}
                  />
                  {patientForm.formState.errors.email && (
                    <p className="text-sm text-destructive">{patientForm.formState.errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="patient-password">Password</Label>
                  <Input
                    id="patient-password"
                    type="password"
                    placeholder="••••••••"
                    {...patientForm.register('password')}
                  />
                  {patientForm.formState.errors.password && (
                    <p className="text-sm text-destructive">{patientForm.formState.errors.password.message}</p>
                  )}
                </div>

                <Button type="submit" className="w-full" disabled={patientForm.formState.isSubmitting}>
                  {patientForm.formState.isSubmitting ? 'Signing in...' : 'Sign In as Patient'}
                </Button>

                <div className="mt-4 rounded-md bg-blue-50 p-3 text-sm text-blue-900 dark:bg-blue-950/50 dark:text-blue-200">
                  <p className="font-medium">For emergencies, call 911</p>
                  <p className="mt-1 text-xs">This portal is for viewing your care progress only.</p>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slide-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
          }
          50% {
            box-shadow: 0 0 40px rgba(59, 130, 246, 0.6);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }

        .animate-slide-in-up {
          animation: slide-in-up 0.8s ease-out;
        }

        .animate-scale-in {
          animation: scale-in 0.6s ease-out;
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }

        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }

        .stagger-animation > * {
          opacity: 0;
          animation: slide-in-up 0.6s ease-out forwards;
        }

        .stagger-animation > *:nth-child(1) { animation-delay: 0.1s; }
        .stagger-animation > *:nth-child(2) { animation-delay: 0.2s; }
        .stagger-animation > *:nth-child(3) { animation-delay: 0.3s; }
        .stagger-animation > *:nth-child(4) { animation-delay: 0.4s; }
        .stagger-animation > *:nth-child(5) { animation-delay: 0.5s; }
        .stagger-animation > *:nth-child(6) { animation-delay: 0.6s; }
      `}</style>
    </div>
  )
}
